/** @jsxImportSource theme-ui */
import classNames from 'classnames';
import React, { useMemo } from 'react';
import { ThemeUIStyleObject } from 'theme-ui';
import { v4 } from 'uuid';

import theme from 'theme';
import { focusStyles } from 'utils/cssHelpers';

import { BdIcon } from 'components/_common/BdIcon';
import { HighlightableSpan } from 'components/_common/HighlightableSpan/HighlightableSpan';

export type CheckboxState = 'none' | 'checked' | 'indeterminate';

const Styles: ThemeUIStyleObject = {
  display: 'flex',
  userSelect: 'none',
  '& .hidden-checkbox': { width: '0', height: '0', appearance: 'initial', border: '0px' }, // no height/width instead of no display for tab target
  '& > label': { display: 'flex', alignItems: 'center', cursor: 'pointer' },
  '& .styled-checkbox': {
    height: '26px',
    width: '26px',
    border: `1px solid ${theme.colors.stone20}`,
    borderRadius: '2px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: theme.colors.sea90,
    flexShrink: 0,
  },
  '& .label-text': { ml: '10px', color: theme.colors.stone90 },
  '&.disabled': {
    '& > label': { cursor: 'initial' },
    '& .styled-checkbox': {
      color: theme.colors.stone50,
      bg: theme.colors.stone10,
    },
    '& .label-text': { color: theme.colors.stone50 },
  },
  '&:hover:not(.disabled)': { '& .styled-checkbox': { borderColor: theme.colors.deepSea50 } },
  '&:focus-within:not(.no-tab)': focusStyles(),
};

type BdCheckboxProps = {
  value: CheckboxState;
  label: string;
  disabled?: boolean;
  /** Removes the label text and places the text as the aria-label */
  checkboxOnly?: boolean;
  /** **THIS SHOULD ONLY BE TRUE IN CASES OF THE CHECKBOX BEING USED IN A DROPDOWN** */
  disableTabTarget?: boolean;
  onChange?: () => void;
};

const BdCheckbox: React.FC<BdCheckboxProps> = ({
  value,
  label,
  disabled = false,
  checkboxOnly = false,
  disableTabTarget = false,
  onChange,
}) => {
  const guid = useMemo(() => v4(), []);
  const instanceId = `bd-checkbox-${guid}`;

  return (
    <div
      className={classNames({ checkbox: true, disabled: disabled, 'no-tab': disableTabTarget })}
      sx={Styles}
    >
      <input
        id={instanceId}
        type="checkbox"
        value={value}
        className="hidden-checkbox"
        aria-label={checkboxOnly ? label : undefined}
        tabIndex={disableTabTarget ? -1 : 0}
        onChange={!disabled ? onChange : null}
      />
      <label htmlFor={instanceId}>
        <div className="styled-checkbox">
          {value !== 'none' && (
            <BdIcon name={value === 'checked' ? 'icon-checkmark' : 'icon-minimize'} bold nomargin />
          )}
        </div>
        {!checkboxOnly && (
          <HighlightableSpan
            text={label}
            className="label-text bd-typography bd-typography-body-small-regular"
          />
        )}
      </label>
    </div>
  );
};

export default BdCheckbox;
